import React, { useRef, useState, useEffect, memo } from "react";
import PropTypes from "prop-types";
import _findIndex from "lodash/findIndex";
import { Trans } from "@lingui/macro";

import { Button, BUTTON_SIZES, BUTTON_TYPES } from "widgets/inputs";
import { Toast } from "widgets/feedback";
import {
  SingleSelectDropdown,
  SingleSelectDropdownItem
} from "widgets/singleselect";

import { RemoteLookupCard } from "@/formwidgets/remotelookup/remotelookupcard";
import { Base } from "../base";
import {
  parseXML,
  isEmpty,
  getRLookupSearchData,
  formatRLookupData
  // getSortedObj
} from "@/formwidgets/remotelookup/lookuputils";
import { Search } from "@/icons";

import styles from "./remotelookup.css";
// import CommonStyles from "../../datasheet.css"; // commented temporarily

export function RemoteLookupEditor(props) {
  const { column, value, row } = props;
  const baseRef = useRef(null);
  const LookupTriggerType = column.modelConfig.RemoteLookupTrigger;
  const [isLoading, setIsLoading] = useState(false);
  const [originalData, setOriginalData] = useState(value);
  const [lookupData, updateLookupData] = useState([]);
  const [rLookupCopy, setLookupCopy] = useState([]);
  const findType = {
    isText:
      column &&
      column.modelConfig &&
      column.modelConfig.RemoteLookupResultType === "Text",
    isXML:
      column &&
      column.modelConfig &&
      column.modelConfig.RemoteLookupResultType === "XML",
    isJSON:
      column &&
      column.modelConfig &&
      column.modelConfig.RemoteLookupResultType === "JSON"
  };
  const formatValue = (value) => {
    // if (findType.isXML && value && typeof value === "string") {
    if (findType.isXML && value) {
      return parseXML(value);
    }
    // Ignoring sorting for both JSON and Text responses
    // else if (findType.isJSON && value) {
    //   return getSortedObj(value);
    // }
    return value;
  };

  const [currentValue, setCurrentValue] = useState(formatValue(value));
  useEffect(
    function onValueChange() {
      let formattedValue = formatValue(value);
      setCurrentValue(formattedValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value]
  );

  const getRemoteLookupData = () => {
    setIsLoading(true);
    column.dataSource({ value, row }).then(
      (data) => {
        let formattedValue = formatRLookupData(
          data,
          column.modelConfig.RemoteLookupTrigger,
          column.modelConfig.RemoteLookupResultType
        );
        setOriginalData(data);
        updateLookupData(formattedValue);
        if (LookupTriggerType === "Auto") {
          let updateValue = findType.isXML ? data : formattedValue;
          baseRef.current.dataChanged(updateValue);
          baseRef.current.dataCompleted();
        } else {
          setLookupCopy(formattedValue);
        }

        setIsLoading(false);
      },
      (err) => {
        Toast.showError({
          message: err.message
        });
      }
    );
  };
  const onManualDataSelect = function onManualDataSelectFunction(value) {
    if (LookupTriggerType !== "Auto" && findType.isXML) {
      let index = _findIndex(rLookupCopy, value);
      value = originalData[index];
    }
    setCurrentValue(value);
    baseRef.current.dataChanged(value);
    baseRef.current.dataCompleted();
  };
  const clearData = () => {
    baseRef.current.dataChanged("");
    baseRef.current.dataCompleted();
  };
  const getFilterData = function getFilterDataFunction(searchObj) {
    let searchText = searchObj.search;
    let result =
      searchText === ""
        ? rLookupCopy
        : getRLookupSearchData(rLookupCopy, searchText);
    updateLookupData(result);
  };

  return LookupTriggerType && LookupTriggerType === "Auto" ? (
    <Base {...props} ref={baseRef} onEditing={(e) => {}} type="select">
      {!isEmpty(currentValue) ? (
        <RemoteLookupCard
          lookupData={currentValue}
          isDropdownOpen={false}
          onClick={clearData}
          remoteLookupResultType={column.modelConfig.RemoteLookupResultType}
          formattedData={rLookupCopy}
          originalData={originalData}
          field={column}
        />
      ) : (
        <div className={styles.remotelookup}>
          <Button
            size={BUTTON_SIZES.TINY}
            type={!isLoading ? BUTTON_TYPES.TERTIARY : BUTTON_TYPES.PRIMARY}
            onClick={getRemoteLookupData}
            isLoading={isLoading}
          >
            <Trans>Load Data</Trans>
          </Button>
        </div>
      )}
    </Base>
  ) : (
    <Base {...props} ref={baseRef} onEditing={(e) => {}} type="select">
      {findType.isText && (
        <SingleSelectDropdown
          className={`${styles.rLookupDropdownInput}`}
          rightIcon={<Search />}
          dataSource={lookupData}
          onChange={onManualDataSelect}
          value={currentValue}
          onSearchDebounced={getFilterData}
          onOpen={getRemoteLookupData}
          matchWidth={true}
          minDropdownWidth="40rem"
        />
      )}
      {(findType.isJSON || findType.isXML) && (
        <React.Fragment>
          {!currentValue ? (
            <SingleSelectDropdown
              value={currentValue}
              dataSource={lookupData}
              rightIcon={<Search />}
              onOpen={getRemoteLookupData}
              onSearch={getFilterData}
              onChange={onManualDataSelect}
              // type={DROPDOWN_TYPE.MINIMAL}
              className={`${styles.rLookupDropdownInput}`}
              dropdownClassName={styles.lookupDropdown}
              isLoading={isLoading}
              matchWidth={true}
              minDropdownWidth="40rem"
              itemRenderer={(itemValue) => {
                return (
                  <SingleSelectDropdownItem
                    label="Name"
                    value={itemValue._counter}
                  >
                    <div>
                      <RemoteLookupCard
                        lookupData={itemValue}
                        isDropdownOpen={true}
                        remoteLookupResultType={
                          column.modelConfig.RemoteLookupResultType
                        }
                        formattedData={rLookupCopy}
                        originalData={originalData}
                        field={column}
                      />
                    </div>
                  </SingleSelectDropdownItem>
                );
              }}
            />
          ) : (
            <RemoteLookupCard
              lookupData={currentValue}
              // isDropdownOpen={false}
              remoteLookupResultType={column.modelConfig.RemoteLookupResultType}
              formattedData={rLookupCopy}
              originalData={originalData}
              onClick={clearData}
              field={column}
            />
          )}
        </React.Fragment>
      )}
    </Base>
  );
}

RemoteLookupEditor.propTypes = {
  row: PropTypes.any,
  value: PropTypes.any,
  column: PropTypes.any
};

export const RemoteLookup = memo(RemoteLookupEditor);
